import { FormHelper } from '@presentation/components/FormHelper';
import { BAREME_TVA_PAR_DEFAUT, optionsTvaPourDate, tauxTvaA, type TauxTvaDate } from '@domain/fiscalite/BaremeTva';
import { parseFlexibleDateToIso } from '@shared/utils/fmtDate';
import { PrecompteISService } from '@domain/fiscalite/PrecompteISService';
import { PLAN_COMPTABLE_OHADA } from '@shared/constants/PlanComptable';
import { roundFCFA } from '@shared/utils/round';
import type { PrecompteType } from '@shared/types/Facture';

const EXTRA_COMPTES_CHARGES: Record<string, string> = {
  '622': '622 — Achats non stockés',
  '6011': '6011 — Achats de marchandises',
  '6041': '6041 — Fournitures consommables',
};

const EXTRA_COMPTES_PRODUITS: Record<string, string> = {
  '7060': '7060 — Prestations de services',
  '706': '706 — Services vendus',
  '701': '701 — Ventes de marchandises',
};

export const DEVISE_OPTIONS = [
  { value: 'XAF', label: 'XAF — Franc CFA' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'USD', label: 'USD — Dollar US' },
];

/** Taux proposés, dérivés du barème de TVA daté (défauts : 18 % / 10 % / 5 % / 0 %). */
export const TVA_OPTIONS = optionsTvaPourDate(new Date().toISOString().slice(0, 10));

/** Les libellés sont dérivés des taux appliqués pour éviter toute divergence affichage/calcul. */
function labelPrecompte(base: string, type: Exclude<PrecompteType, ''>): string {
  const pct = (PrecompteISService.tauxFor(type, { date: new Date().toISOString().slice(0, 10) }) * 100)
    .toFixed(2)
    .replace(/\.?0+$/, '')
    .replace('.', ',');
  return `${base} — ${pct} %`;
}

export const PRECOMPTE_OPTIONS: { value: PrecompteType; label: string }[] = [
  { value: '', label: 'Aucun précompte' },
  { value: 'loyer', label: labelPrecompte('Loyer (art. 178 bis)', 'loyer') },
  { value: 'honoraires_nr', label: labelPrecompte('Honoraires non-résidents', 'honoraires_nr') },
  { value: 'honoraires_res', label: labelPrecompte('Honoraires résidents non assujettis à la TVA', 'honoraires_res') },
  { value: 'prestations', label: labelPrecompte('Prestations de service non assujetties à la TVA', 'prestations') },
];

function ohadaOptions(
  extras: Record<string, string>,
  filter: (classe: number) => boolean,
): { value: string; label: string }[] {
  const seen = new Set<string>();
  const out: { value: string; label: string }[] = [];
  for (const [code, label] of Object.entries(extras)) {
    seen.add(code);
    out.push({ value: code, label });
  }
  for (const c of Object.values(PLAN_COMPTABLE_OHADA)) {
    if (!filter(c.classe) || seen.has(c.code)) continue;
    seen.add(c.code);
    out.push({ value: c.code, label: `${c.code} — ${c.libelle}` });
  }
  return out.sort((a, b) => a.value.localeCompare(b.value));
}

export function compteChargeOptions(): { value: string; label: string }[] {
  return ohadaOptions(EXTRA_COMPTES_CHARGES, (cl) => cl === 6);
}

export function compteProduitOptions(): { value: string; label: string }[] {
  return ohadaOptions(EXTRA_COMPTES_PRODUITS, (cl) => cl === 7);
}

export function fmtTotals(n: number): string {
  return n.toLocaleString('fr-FR', { maximumFractionDigits: 0 });
}

export function calcLineTotals(
  qty: number,
  pu: number,
  tva: number,
): { ht: number; tva: number; ttc: number } {
  const ht = roundFCFA(qty * pu);
  const tvaAmt = roundFCFA(ht * tva);
  return { ht, tva: tvaAmt, ttc: ht + tvaAmt };
}

export function wireLiveTotals(
  form: HTMLFormElement,
  lignesBox: HTMLElement,
  lineClass: string,
  totalsIds: { ht: string; tva: string; ttc: string },
): void {
  const refresh = (): void => {
    let ht = 0;
    let tva = 0;
    for (const row of lignesBox.querySelectorAll<HTMLElement>(`.${lineClass}`)) {
      const idx = row.dataset.idx ?? '0';
      const qty = Number(form.querySelector<HTMLInputElement>(`[name="qty_${idx}"]`)?.value || 0);
      const pu = Number(form.querySelector<HTMLInputElement>(`[name="pu_${idx}"]`)?.value || 0);
      const taux = Number(form.querySelector<HTMLSelectElement>(`[name="tva_${idx}"]`)?.value || 0);
      const line = calcLineTotals(qty, pu, taux);
      ht += line.ht;
      tva += line.tva;
    }
    const htEl = form.querySelector(`#${totalsIds.ht}`);
    const tvaEl = form.querySelector(`#${totalsIds.tva}`);
    const ttcEl = form.querySelector(`#${totalsIds.ttc}`);
    if (htEl) htEl.textContent = fmtTotals(ht);
    if (tvaEl) tvaEl.textContent = fmtTotals(tva);
    if (ttcEl) ttcEl.textContent = fmtTotals(ht + tva);
  };
  form.addEventListener('input', refresh);
  form.addEventListener('change', refresh);
  refresh();
}

export interface OptionTva {
  value: string;
  label: string;
}

/** Liste de taux proposée à une date de facture, selon le barème de l'entreprise, et taux présélectionné (taux normal). */
export function optionsEtDefautTva(
  date: string,
  bareme: readonly TauxTvaDate[] = BAREME_TVA_PAR_DEFAUT,
): { options: OptionTva[]; defaut: string } {
  return {
    options: optionsTvaPourDate(date, bareme),
    defaut: String(tauxTvaA('normal', date, bareme)),
  };
}

function libelleTaux(valeur: string, options: readonly OptionTva[]): string {
  return options.find((o) => o.value === valeur)?.label ?? `${Math.round(Number(valeur) * 10000) / 100} %`.replace('.', ',');
}

/**
 * Applique une nouvelle liste de taux aux lignes déjà saisies : une ligne garde son taux s'il existe encore dans la liste,
 * sinon elle reprend le taux présélectionné. Retourne le message à annoncer (vide si rien n'a changé).
 */
export function appliquerOptionsTvaAuxLignes(
  lignesBox: HTMLElement,
  lineClass: string,
  options: readonly OptionTva[],
  defaut: string,
): string {
  const remplaces: string[] = [];
  const lignes = Array.from(lignesBox.querySelectorAll<HTMLElement>(`.${lineClass}`));
  lignes.forEach((ligne, i) => {
    const idx = ligne.dataset['idx'] ?? '0';
    const select = ligne.querySelector<HTMLSelectElement>(`[name="tva_${idx}"]`);
    if (!select) return;
    const ancienneValeur = select.value;
    const ancienLibelle = select.selectedOptions[0]?.textContent?.trim() ?? libelleTaux(ancienneValeur, options);
    select.innerHTML = options
      .map((o) => `<option value="${o.value}">${o.label.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</option>`)
      .join('');
    if (options.some((o) => o.value === ancienneValeur)) {
      select.value = ancienneValeur;
    } else {
      select.value = defaut;
      remplaces.push(
        `ligne ${i + 1} (${ancienLibelle} remplacé par ${libelleTaux(defaut, options)})`,
      );
    }
  });
  if (remplaces.length === 0) return '';
  return `À cette date, certains taux de TVA n’existent plus : ${remplaces.join(' ; ')}. Vérifiez les lignes concernées.`;
}

/**
 * Relie la date de la facture au barème de TVA de l'entreprise : à chaque changement de date, la liste des taux
 * de toutes les lignes est recalculée et un message `role="status"` signale les taux remplacés.
 */
export function lierBaremeAuxFactures(opts: {
  form: HTMLFormElement;
  lignesBox: HTMLElement;
  lineClass: string;
  dateFieldName: string;
  bareme: readonly TauxTvaDate[];
  statusEl: HTMLElement | null;
  aujourdhui: string;
}): { courant(): { options: OptionTva[]; defaut: string } } {
  let courant = optionsEtDefautTva(opts.aujourdhui, opts.bareme);
  const lireDate = (): string | null => {
    const champ = opts.form.querySelector<HTMLInputElement>(`[name="${opts.dateFieldName}"]`);
    if (!champ) return null;
    return parseFlexibleDateToIso(champ.value) ?? (/^\d{4}-\d{2}-\d{2}$/.test(champ.value) ? champ.value : null);
  };
  const recalculer = (): void => {
    const date = lireDate();
    if (!date) return;
    let suivant: { options: OptionTva[]; defaut: string };
    try {
      suivant = optionsEtDefautTva(date, opts.bareme);
    } catch {
      return;
    }
    courant = suivant;
    const message = appliquerOptionsTvaAuxLignes(opts.lignesBox, opts.lineClass, courant.options, courant.defaut);
    if (opts.statusEl) opts.statusEl.textContent = message;
    opts.form.dispatchEvent(new Event('input', { bubbles: true }));
  };
  const champ = opts.form.querySelector<HTMLInputElement>(`[name="${opts.dateFieldName}"]`);
  const natif = opts.form.querySelector<HTMLInputElement>(`.nx-date-native[data-for="${opts.dateFieldName}"]`);
  for (const el of [champ, natif]) {
    el?.addEventListener('change', recalculer);
    el?.addEventListener('blur', recalculer);
  }
  champ?.addEventListener('input', recalculer);
  return { courant: () => courant };
}

export function factureLineRow(
  idx: number,
  lineClass: string,
  compteOptions: { value: string; label: string }[],
  defaultCompte: string,
  tvaOptions: readonly OptionTva[] = TVA_OPTIONS,
  tvaDefaut = '0.18',
): string {
  return `
    <div class="${lineClass}" data-idx="${idx}" style="display:grid;grid-template-columns:2fr 1fr 1fr 1fr 1fr 1fr auto;gap:8px;margin-bottom:8px;align-items:end;">
      ${FormHelper.field(`desc_${idx}`, 'Description', 'text', { required: true })}
      ${FormHelper.field(`qty_${idx}`, 'Qté', 'number', { required: true, min: 1, value: 1 })}
      ${FormHelper.field(`pu_${idx}`, 'P.U. HT', 'number', { required: true, min: 0 })}
      ${FormHelper.select(`tva_${idx}`, 'TVA', [...tvaOptions], { required: true, value: tvaDefaut })}
      ${FormHelper.select(`cpt_${idx}`, 'Compte OHADA', compteOptions, { required: true, value: defaultCompte })}
      ${FormHelper.field(`axe_${idx}`, 'Axe CDC', 'text', { placeholder: 'Centre de coût' })}
      <button type="button" data-remove="${idx}" style="padding:8px 10px;margin-bottom:2px;border:1px solid #e1e4e8;border-radius:4px;background:white;cursor:pointer;" title="Supprimer">×</button>
    </div>
  `;
}

export function factureTotalsBar(ids: { ht: string; tva: string; ttc: string }): string {
  return `
    <div style="display:flex;justify-content:flex-end;gap:24px;margin-top:12px;padding:12px;background:#f6f8fa;border-radius:6px;font-size:0.9rem;">
      <span>Total HT : <strong id="${ids.ht}">0</strong></span>
      <span>TVA : <strong id="${ids.tva}">0</strong></span>
      <span>Total TTC : <strong id="${ids.ttc}">0</strong></span>
    </div>
  `;
}
